import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, MapPin, Phone, Mail, MessageCircle, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

const WHATSAPP_URL = 'https://wa.me/917621003708';
const INSTAGRAM_URL = 'https://www.instagram.com/bandhani_by_abdulahadkhatri';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setSubmitting(true);
      const resp = await api.post('/newsletter/subscribe', { email: email.trim() });
      setSubscribed(true);
      toast.success(resp.data.message);
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Subscription failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-secondary py-16 md:py-24 mt-32" data-testid="main-footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">

        {/* Newsletter Banner */}
        <div className="mb-16 pb-12 border-b border-border/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl md:text-4xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Join Our Heritage Circle
              </h3>
              <p className="text-sm text-muted-foreground">
                Be the first to know about new collections, artisan stories, and exclusive early-access drops.
              </p>
            </div>
            {subscribed ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 p-4 rounded-full" data-testid="newsletter-success">
                <Check className="h-5 w-5 text-green-700 flex-shrink-0" />
                <p className="text-sm text-green-900">You're subscribed. Welcome to the family.</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="newsletter-form">
                <Input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-full flex-1"
                  disabled={submitting}
                  data-testid="newsletter-email-input"
                />
                <Button type="submit" disabled={submitting} className="rounded-full px-6 uppercase tracking-widest text-xs" data-testid="newsletter-submit-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
                </Button>
              </form>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              BANDHANI HERITAGE
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Preserving the ancient art of Bandhani tie-dye textiles with modern luxury.
            </p>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest font-bold mb-4">Shop</h4>
            <ul className="space-y-2">
              <li><Link to="/products" className="text-sm hover:text-primary transition-colors">All Products</Link></li>
              <li><Link to="/products?category=Sarees" className="text-sm hover:text-primary transition-colors">Sarees</Link></li>
              <li><Link to="/products?category=Dupattas" className="text-sm hover:text-primary transition-colors">Dupattas</Link></li>
              <li><Link to="/products?category=Suits" className="text-sm hover:text-primary transition-colors">Suits</Link></li>
              <li><Link to="/wholesale" className="text-sm hover:text-primary transition-colors font-medium" data-testid="footer-wholesale-link">Wholesale Inquiry</Link></li>
              <li><Link to="/book-call" className="text-sm hover:text-primary transition-colors font-medium" data-testid="footer-book-call-link">Book a Call</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest font-bold mb-4">About</h4>
            <ul className="space-y-2">
              <li><Link to="/#story" className="text-sm hover:text-primary transition-colors">Our Story</Link></li>
              <li><Link to="/#craftsmanship" className="text-sm hover:text-primary transition-colors">Craftsmanship</Link></li>
              <li><Link to="/wholesale" className="text-sm hover:text-primary transition-colors">Wholesale Terms</Link></li>
              <li><Link to="/return-policy" className="text-sm hover:text-primary transition-colors" data-testid="footer-return-policy-link">Return &amp; Cancellation</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest font-bold mb-6">Contact Us</h4>
            <div className="space-y-3 mb-6">
              <a href="tel:+917621003708" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                <Phone className="h-4 w-4" />
                +91 7621003708
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                data-testid="footer-whatsapp-link"
              >
                <MessageCircle className="h-4 w-4" />
                Chat on WhatsApp
              </a>
              <a
                href="https://maps.app.goo.gl/reTAXCGUiYq8qLRJA?g_st=ac"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 text-sm hover:text-primary transition-colors"
              >
                <MapPin className="h-4 w-4 mt-1 flex-shrink-0" />
                <span>ANAM RING ROAD,<br />BHUJ-KUTCH, GUJARAT<br />370001</span>
              </a>
            </div>

            <h4 className="text-xs uppercase tracking-widest font-bold mb-4">Follow Us</h4>
            <div className="flex gap-4">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
                data-testid="footer-instagram-link"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com/share/1HnsTwGdN4/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle className="h-5 w-5" />
              </a>
              <a
                href="mailto:hello@bandhaniheritage.com"
                className="hover:text-primary transition-colors"
                aria-label="Email"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-border mt-12 pt-8 text-center">
          <p className="text-xs text-muted-foreground">© 2026 Bandhani by Abdul Ahad Khatri. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
